import { Schema } from "effect"
import {
  AgentId,
  AreaId,
  ArtifactId,
  CommentId,
  EdgeId,
  GoalId,
  InitiativeId,
  IntakeId,
  KpiId,
  MemoryItemId,
  Millis,
  PolicyId,
  ProposalId,
  RepoId,
  RunId,
  RunnerId,
  Seq,
  TaskId,
  UserId,
} from "./ids"

// #region actors
export const Visibility = Schema.Literals(["workspace", "private"])
export const Taint = Schema.Literals(["clean", "tainted"])
export type Taint = typeof Taint.Type
export const AgentKind = Schema.Literals(["claude", "codex"])
export type AgentKind = typeof AgentKind.Type
export const ModelProvider = Schema.Literals(["anthropic", "openai"])
export const Effort = Schema.Literals(["low", "medium", "high", "max"])
export type Effort = typeof Effort.Type
/** A model and effort for one CLI family; null = the profile's override, else the CLI default (settings wave §5.1). */
export const ModelChoice = Schema.Struct({
  model: Schema.NullOr(Schema.String.check(Schema.isMaxLength(128))),
  effort: Schema.NullOr(Effort),
})
export type ModelChoice = typeof ModelChoice.Type
/** Model and effort per CLI family, for a run whose family the lease decides (`Run.byAgent`, `JobTemplate.byAgent`). */
export const ModelsByAgent = Schema.Struct({
  claude: Schema.optionalKey(ModelChoice),
  codex: Schema.optionalKey(ModelChoice),
})
export type ModelsByAgent = typeof ModelsByAgent.Type

/** Platform processes. None of them can issue a task-, initiative- or goal-creating command (structural, §8.1). */
export const SystemProcess = Schema.Literals([
  "scheduler", //    dispatches runs for ready tasks and retries attempts; never creates tasks
  "lease_reaper", // ends a started run whose lease expired (runner_lost)
  "watchdog", //     stall and loop detection; escalates only (question proposal)
  "expirer", //      proposal expiry (denied, or the question's default option)
  "membership", //   mirrors Better Auth membership and the Mayor record into project DOs
  "kpi_sync", //     records KPI samples from connectors (Phase 3)
  "gateway", //      reports outward executions (Phase 2)
  "upgrade", //      one-off state upgrades after a deploy (policy backfill); never creates work
])

/**
 * Who did something. Never sent by a client: stamped from the authenticated credential (session, runner token,
 * per-run MCP token, internal DO identity). `onBehalfOf` is the agent's owner, copied from the roster by the server.
 */
export const Actor = Schema.TaggedUnion({
  user: { userId: UserId },
  /** A persistent agent (lead, role agent or Mayor) inside one of its turn runs, on any mind. */
  agent: { agentId: AgentId, runId: RunId, onBehalfOf: UserId },
  /** A temporary run with no persistent identity: worker or validator. `agentId` is the task's assignee. */
  worker: { runId: RunId, taskId: TaskId, agentId: AgentId, onBehalfOf: UserId },
  /** Run lifecycle facts relayed by the runner hub (lease, start, end, milestones). Never graph edits. */
  runner: { runnerId: RunnerId },
  system: { process: SystemProcess },
})
export type Actor = typeof Actor.Type
// #endregion actors

// #region meta
/** On every Workspace-level record. `ownerId` is always a human; steer rights and approval routing key off it. */
export const Owned = { ownerId: UserId, visibility: Visibility } as const

/** On every Project-level record (graph, ledger, memory, runs). Attribution is separate from ownership. */
export const RecordMeta = {
  ...Owned,
  createdBy: Actor,
  createdAt: Millis,
  updatedBy: Actor,
  updatedAt: Millis,
  /** Seq of the last event that touched this record. Commands name it in `expect` for optimistic concurrency. */
  lastSeq: Seq,
} as const

/**
 * On records whose free text is written whole (memory items, artifacts, comments, proposals): the seq of the event
 * that last wrote that content. Status changes move `lastSeq`, never this, so `Redacted` still finds the record.
 */
export const ContentMeta = { contentSeq: Seq } as const

/** Pin: agents can't move it (rank, parent, assignee). Lock: agents can't edit it at all (they may comment or propose). */
export const Hold = Schema.TaggedUnion({
  none: {},
  pinned: { by: UserId, at: Millis, note: Schema.NullOr(Schema.String) },
  locked: { by: UserId, at: Millis, note: Schema.NullOr(Schema.String) },
})

/** Graph nodes (what the canvas draws and selection carries across views). */
export const NodeRef = Schema.TaggedUnion({
  goal: { id: GoalId },
  kpi: { id: KpiId },
  initiative: { id: InitiativeId },
  task: { id: TaskId },
  area: { id: AreaId },
  agent: { id: AgentId },
})
export type NodeRef = typeof NodeRef.Type

/** Anything addressable in a project: nodes plus edges, ledger items, memory, artifacts, comments, runs, policies. */
export const EntityRef = Schema.TaggedUnion({
  goal: { id: GoalId },
  kpi: { id: KpiId },
  initiative: { id: InitiativeId },
  task: { id: TaskId },
  area: { id: AreaId },
  agent: { id: AgentId },
  edge: { id: EdgeId },
  proposal: { id: ProposalId },
  memory: { id: MemoryItemId },
  artifact: { id: ArtifactId },
  comment: { id: CommentId },
  run: { id: RunId },
  policy: { id: PolicyId },
  intake: { id: IntakeId },
})
export type EntityRef = typeof EntityRef.Type

/** Work nodes: the only endpoints of `blocks` / `informs` edges and the only things that carry a rank among siblings. */
export const WorkRef = Schema.TaggedUnion({ initiative: { id: InitiativeId }, task: { id: TaskId } })
export const RankedRef = Schema.TaggedUnion({
  goal: { id: GoalId },
  initiative: { id: InitiativeId },
  task: { id: TaskId },
})

/** A mention target or reviewer. People and agents share one @handle namespace per project. */
export const Principal = Schema.TaggedUnion({ user: { userId: UserId }, agent: { agentId: AgentId } })

export const Risk = Schema.Literals(["low", "medium", "high"])
export const Reversibility = Schema.Literals(["reversible", "costly", "irreversible"])
/** Attention tiers: blocking pushes now, soon goes to Needs-you (batched push), fyi to the digest, ambient to the canvas. */
export const Tier = Schema.Literals(["blocking", "soon", "fyi", "ambient"])

/** Why a claim holds. Shared by run outcomes, validator verdicts, memory items and bet verdicts. */
export const Evidence = Schema.TaggedUnion({
  artifact: { artifactId: ArtifactId },
  check: { name: Schema.String, passed: Schema.Boolean, detail: Schema.NullOr(Schema.String) },
  pull_request: { url: Schema.String, headSha: Schema.String },
  commit: { repoId: RepoId, sha: Schema.String },
  url: { url: Schema.String, note: Schema.String }, // tainted by definition
  run: { runId: RunId },
  kpi: { kpiId: KpiId, from: Millis, to: Millis },
  statement: { userId: UserId, at: Millis }, // "remember that…"
})
// #endregion meta
